import React, { useState } from 'react';
import { X, Mail, Shield, Download, AlertCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { doc, getDoc, addDoc, collection } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import EmailVerificationModal from '../EmailVerificationModal';
import { getResourceById, downloadPdf } from '../../services/resourceService';

interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceId: string | null;
}

const loginSchema = z.object({
  email: z.string()
    .min(1, 'L\'email est requis')
    .email('Format d\'email invalide'),
  password: z.string()
    .min(6, 'Le mot de passe doit contenir au moins 6 caractères')
});

type FormData = z.infer<typeof loginSchema>;

const EmailModal: React.FC<EmailModalProps> = ({ isOpen, onClose, resourceId }) => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const { currentUser, signIn, signUp, signInWithGoogle } = useAuth();

  const { register, handleSubmit, formState: { errors }, reset, setError } = useForm<FormData>({
    resolver: zodResolver(loginSchema)
  });

  if (!isOpen || !resourceId) return null;

  const handleDownload = async () => {
    try {
      console.log("Downloading resource with ID:", resourceId);
      
      // Récupérer les données de la ressource
      const resource = await getResourceById(resourceId);
      
      if (!resource) {
        throw new Error('Ressource non trouvée');
      }
      
      console.log("Resource data:", resource);
      
      // Utiliser la nouvelle fonction de téléchargement compatible avec Safari
      const fileName = `${resource.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      await downloadPdf(resource.pdfUrl, fileName);

      // Enregistrer le téléchargement si l'utilisateur est connecté
      if (currentUser) {
        try {
          await addDoc(collection(db, 'downloads'), {
            userId: currentUser.uid,
            email: currentUser.email,
            resourceId,
            resourceTitle: resource.title,
            timestamp: new Date()
          });
          console.log("Download logged successfully");
        } catch (error) {
          console.error("Error logging download:", error);
          // Continue even if logging fails
        }
      }
      
      toast.success('Téléchargement lancé !');
      onClose();
      
    } catch (error: any) {
      console.error("Error during download:", error);
      if (error.code === 'storage/object-not-found') {
        toast.error('Le fichier n\'est pas disponible pour le moment');
      } else {
        toast.error('Erreur lors du téléchargement');
      }
    }
  };

  const onSubmit = async (data: FormData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (isRegistering) {
        await signUp(data.email, data.password);
        setPendingEmail(data.email);
        setShowVerificationModal(true);
      } else {
        try {
          await signIn(data.email, data.password);
          handleDownload();
        } catch (error: any) {
          if (error.message === 'EMAIL_NOT_VERIFIED') {
            setPendingEmail(data.email);
            setShowVerificationModal(true);
            return;
          }
          if (error.message === 'SIGNUP_REQUIRED') {
            setIsRegistering(true);
            setError('email', {
              type: 'manual',
              message: 'Aucun compte trouvé avec cet email. Veuillez créer un compte.'
            });
            return;
          }
          throw error;
        }
      }
      reset();
    } catch (error: any) {
      console.error("Authentication error:", error);
      if (error.code === 'auth/email-already-in-use') {
        setIsRegistering(false);
        toast.error('Un compte existe déjà avec cet email. Veuillez vous connecter.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (currentUser?.emailVerified) {
    handleDownload();
    return null;
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X size={24} />
          </button>

          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#9a6ebd]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-[#9a6ebd]" />
            </div>
            <h3 className="text-xl font-semibold text-[#323c5f] mb-2">
              {isRegistering ? 'Créer un compte' : 'Connexion'}
            </h3>
            <p className="text-gray-600">
              {isRegistering 
                ? 'Créez votre compte pour accéder à toutes nos ressources'
                : 'Connectez-vous pour accéder à nos ressources'
              }
            </p>
          </div>

          <div className="mb-6">
            <button
              onClick={async () => {
                try {
                  await signInWithGoogle();
                  handleDownload();
                } catch (error) {
                  console.error("Google sign-in error:", error);
                }
              }}
              className="w-full flex items-center justify-center gap-3 px-6 py-3 border-2 border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google"
                className="w-5 h-5"
              />
              <span className="text-gray-700">Continuer avec Google</span>
            </button>
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">ou</span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {errors.root && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                {errors.root.message}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  {...register('email')}
                  className={`w-full rounded-lg border-2 pl-10 pr-4 py-2.5 text-[#323c5f]
                    ${errors.email 
                      ? 'border-red-300 focus:border-red-300 focus:ring focus:ring-red-200' 
                      : 'border-[#9a6ebd] focus:border-[#9a6ebd] focus:ring focus:ring-[#9a6ebd]/50'
                    }`}
                  placeholder="exemple@domaine.com"
                />
                <Mail 
                  className={`absolute left-3 top-1/2 transform -translate-y-1/2
                    ${errors.email ? 'text-red-400' : 'text-[#9a6ebd]'}`} 
                  size={18} 
                />
              </div>
              {errors.email && (
                <div className="mt-2 flex items-start text-sm text-red-600">
                  <AlertCircle className="mr-1 flex-shrink-0 mt-0.5" size={14} />
                  <span>{errors.email.message}</span>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  {...register('password')}
                  className={`w-full rounded-lg border-2 pl-10 pr-12 py-2.5 text-[#323c5f]
                    ${errors.password 
                      ? 'border-red-300 focus:border-red-300 focus:ring focus:ring-red-200' 
                      : 'border-[#9a6ebd] focus:border-[#9a6ebd] focus:ring focus:ring-[#9a6ebd]/50'
                    }`}
                  placeholder="••••••••"
                />
                <Lock 
                  className={`absolute left-3 top-1/2 transform -translate-y-1/2
                    ${errors.password ? 'text-red-400' : 'text-[#9a6ebd]'}`} 
                  size={18} 
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <div className="mt-2 flex items-start text-sm text-red-600">
                  <AlertCircle className="mr-1 flex-shrink-0 mt-0.5" size={14} />
                  <span>{errors.password.message}</span>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-[#9a6ebd] text-white px-6 py-3 rounded-lg hover:bg-[#8a5dad] transition-colors
                ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {isRegistering ? 'Création en cours...' : 'Connexion en cours...'}
                </div>
              ) : (
                <>
                  <Download className="inline-block mr-2" size={20} />
                  {isRegistering ? 'Créer un compte' : 'Se connecter'}
                </>
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setIsRegistering(!isRegistering);
                  reset();
                }}
                className="text-[#9a6ebd] hover:text-[#8a5dad] text-sm"
              >
                {isRegistering 
                  ? 'Déjà un compte ? Se connecter'
                  : 'Pas encore de compte ? S\'inscrire'
                }
              </button>
            </div>
          </form>
        </div>
      </div>

      <EmailVerificationModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        email={pendingEmail}
      />
    </>
  );
};

export default EmailModal;